/**
 * useActiveSite — read the active site from context.
 *
 * Throws if called outside a SiteProvider. Use this in any screen or
 * hook that needs to filter data by the user's selected site.
 *
 * In the convergence target (plan §6.3) `site` may be null when the
 * caller has no site assignments; callers MUST handle that case
 * (typically by disabling per-site hooks via `enabled: false`).
 */
import { useContext } from 'react';
import { SiteContext, type ActiveSite, type SiteContextValue } from './SiteContext';

export function useActiveSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) {
    throw new Error('useActiveSite must be used within a SiteProvider');
  }
  return ctx;
}

/**
 * Returns the active site id or an empty string when no site is
 * selected. Legacy hooks use this for TanStack Query `queryKey`
 * construction; the corresponding `queryOptions` factory MUST
 * set `enabled: !!siteId` so the query is disabled when no site
 * is selected (plan §6.3).
 */
export function useSafeSiteId(): string {
  return useActiveSite().site?.id ?? '';
}

/**
 * Legacy non-null variant — kept so existing screens that assume a
 * site is always present keep compiling during the migration. New
 * code MUST use `useActiveSite()` and handle the null case.
 *
 * @deprecated use `useActiveSite()` and handle `site: null`.
 */
export function useActiveSiteUnsafe(): { site: ActiveSite; setSite: (site: ActiveSite) => void } {
  const ctx = useActiveSite();
  if (!ctx.site) {
    // Fall back to a placeholder; the hook contract guarantees
    // this never appears in production because SiteProvider
    // reconciles to the first assigned site whenever the API
    // returns a non-empty list.
    return {
      site: { id: '' as never, label: '' },
      setSite: () => {
        /* no-op until sites are available */
      },
    };
  }
  return {
    site: ctx.site,
    setSite: (site) => ctx.setSite(site.id),
  };
}